import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import {
  AuditReasonDialogComponent,
  AuditReasonDialogData
} from '../shared/audit-reason-dialog/audit-reason-dialog.component';

export type AuditAction = 'create' | 'update' | 'delete';

// The blob the client stamps onto a document just before writing/deleting it.
// The cloud-function trigger (controllers/auditLogger.js) reads this to attribute
// the change and capture the reason — Firestore triggers cannot see the acting
// user on their own.
export interface AuditStamp {
  actor: string;
  actorUid: string | null;
  reason: string | null;
  action: AuditAction;
  at: number;
}

export interface AuditLog {
  id: string;
  action: AuditAction;
  entityType: string;
  entityId: string;
  path: string;
  performedBy: string;
  performedByUid: string | null;
  reason: string | null;
  changedFields: string[];
  before: any;
  after: any;
  companyId: string | null;
  createdAt: number;
  timestamp?: any;
}

@Injectable({ providedIn: 'root' })
export class AuditService {

  private actorEmail = 'unknown';
  private actorUid: string | null = null;

  constructor(
    private afs: AngularFirestore,
    private auth: AuthService,
    private dialog: MatDialog
  ) {
    // Cache the acting admin so every stamp is synchronous. Mirrors the pattern
    // used by user.service / system.service.
    this.auth.user$.subscribe(user => {
      if (user) {
        this.actorEmail = (user as any).email || 'unknown';
        this.actorUid = (user as any).uid || null;
      }
    });
  }

  /** Build the `_audit` blob to merge into a write. */
  stamp(action: AuditAction, reason: string | null = null): { _audit: AuditStamp } {
    return {
      _audit: {
        actor: this.actorEmail,
        actorUid: this.actorUid,
        reason: reason || null,
        action,
        // Millisecond timestamp — the trigger uses a change in `at` to tell an
        // admin-initiated write apart from an automated one.
        at: Date.now()
      }
    };
  }

  /**
   * Ask the admin why they are deleting something.
   * Resolves to the reason string, or `null` if they cancelled.
   */
  promptDeleteReason(entityType: string, entityName?: string): Promise<string | null> {
    const data: AuditReasonDialogData = {
      title: `Delete ${entityType}`,
      entityName: entityName || '',
      confirmLabel: 'Delete',
      danger: true
    };
    return this.dialog
      .open(AuditReasonDialogComponent, { width: '440px', data, disableClose: true })
      .afterClosed()
      .toPromise()
      .then(result => (result && typeof result.reason === 'string' ? result.reason : null));
  }

  /** Realtime feed of audit records, newest first. */
  getAuditLogs$(limit = 500): Observable<AuditLog[]> {
    return this.afs
      .collection('auditLogs', ref => ref.orderBy('createdAt', 'desc').limit(limit))
      .snapshotChanges()
      .pipe(
        map(docs => docs.map(doc => ({
          id: doc.payload.doc.id,
          ...(doc.payload.doc.data() as any)
        }) as AuditLog))
      );
  }
}
